import React, { useMemo, useState, useEffect } from "react";


import { API_BASE } from "../config";


function isValidTime(t) {
  return /^\d{2}:\d{2}$/.test((t || "").trim());
}

function timeToMinutes(t) {
  const [h, m] = (t || "00:00").split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function dayLabel(j) {
  return DAYS.find((d) => d.k === Number(j))?.label || `Jour ${j}`;
}



function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

const DAYS = [
  { k: 1, label: "Lundi" },
  { k: 2, label: "Mardi" },
  { k: 3, label: "Mercredi" },
  { k: 4, label: "Jeudi" },
  { k: 5, label: "Vendredi" },
  { k: 6, label: "Samedi" },
  { k: 7, label: "Dimanche" },
];


/* ===========================
   DISPONIBILITES (déjà OK)
=========================== */
export function DisponibilitesMedecin({ user }) {
  const [items, setItems] = useState([]); // {id, jour_semaine, heure_debut, heure_fin, duree_consultation}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // item
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };
  const [toast, setToast] = useState({ type: "", msg: "" });
  

  const [form, setForm] = useState({
    jour_semaine: 1,
    heure_debut: "09:00",
    heure_fin: "12:00",
    duree_consultation: 15,
  });

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${user?.token}`,
  });

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  const fetchDisponibilites = async () => {
    setLoading(true);
    resetMessages();

    try {
      const res = await fetch(`${API_BASE}/medecin/disponibilites`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.msg || "Erreur lors du chargement des disponibilités");
      } else {
        setItems(data.items || []);
      }
    } catch {
      setError("Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisponibilites();
    // eslint-disable-next-line
  }, []);

  const openCreate = () => {
    resetMessages();
    setForm({
      jour_semaine: 1,
      heure_debut: "09:00",
      heure_fin: "12:00",
      duree_consultation: 15,
    });
    setModalOpen(true);
  };

  const validateForm = () => {
    if (!isValidTime(form.heure_debut) || !isValidTime(form.heure_fin)) {
      setError("Heures invalides. Format attendu HH:MM.");
      return false;
    }

    const start = timeToMinutes(form.heure_debut);
    const end = timeToMinutes(form.heure_fin);

    if (end <= start) {
      setError("L'heure de fin doit être après l'heure de début.");
      return false;
    }

    const d = Number(form.duree_consultation);
    if (!d || d < 5 || d > 240) {
      setError("Durée invalide (min 5 min, max 240 min).");
      return false;
    }

    // chevauchement simple côté front (même jour)
    const sameDay = items.filter((x) => Number(x.jour_semaine) === Number(form.jour_semaine));
    for (const x of sameDay) {
      const aStart = timeToMinutes(x.heure_debut);
      const aEnd = timeToMinutes(x.heure_fin);
      if (overlaps(start, end, aStart, aEnd)) {
        setError("Cette plage chevauche une disponibilité existante sur ce jour.");
        return false;
      }
    }

    return true;
  };

  const saveDisponibilite = async () => {
    resetMessages();
    if (!validateForm()) return;

    try {
      const res = await fetch(`${API_BASE}/medecin/disponibilites`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          jour_semaine: Number(form.jour_semaine),
          heure_debut: form.heure_debut,
          heure_fin: form.heure_fin,
          duree_consultation: Number(form.duree_consultation),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.msg || "Erreur lors de l'enregistrement");
        return;
      }

      setModalOpen(false);
      flashSuccess("Disponibilité ajoutée");
      await fetchDisponibilites();
    } catch {
      setError("Impossible de contacter le serveur.");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete?.id) return;

    resetMessages();
    try {
      const res = await fetch(`${API_BASE}/medecin/disponibilites/${confirmDelete.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.msg || "Erreur suppression");
        return;
      }

      setItems((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      setConfirmDelete(null);
      flashSuccess("Disponibilité supprimée");
    } catch {
      setError("Impossible de contacter le serveur.");
    }
  };

  // group by day
  const grouped = useMemo(() => {
    const map = new Map();
    for (const d of DAYS) map.set(d.k, []);
    for (const it of items) {
      const k = Number(it.jour_semaine);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    // sort each group
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => timeToMinutes(a.heure_debut) - timeToMinutes(b.heure_debut));
      map.set(k, arr);
    }
    return map;
  }, [items]);

  return (
    <div className="adminPanel blocagesCompact">
      <div className="adminPanelHeader">
        <div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>


        <button
          className="btnSecondary"
          onClick={async () => {
            await fetchDisponibilites();
            showToast("success", "Disponiblités sont à jour");
          }}
          disabled={loading}
        >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>
          <button className="btnPrimary" onClick={openCreate}>
            + Ajouter une plage
          </button>
        </div>
      </div>

      {error && <div className="adminError">{error}</div>}
      {success && <div className="adminSuccess">{success}</div>}

      <div className="adminGrid">
        {DAYS.map((d) => (
          <div className="adminCard" key={d.k}>
            <div className="adminCardHeader">
              <div className="adminCardTitle">{d.label}</div>
              <div className="adminCardDesc">
                {grouped.get(d.k)?.length ? `${grouped.get(d.k).length} plage(s)` : "Aucune plage"}
              </div>
            </div>

            {grouped.get(d.k)?.length ? (
              <div style={{ display: "grid", gap: 10 }}>
                {grouped.get(d.k).map((it) => (
                  <div
                    key={it.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: 12,
                      borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.08)",
                      background: "rgba(255,255,255,0.6)",
                    }}
                  >
                    <div style={{ display: "grid" }}>
                      <div className="dispoHeure" style={{ fontWeight: 600 }}>
                        {it.heure_debut} → {it.heure_fin}
                      </div>
                      <div className="adminMuted" style={{ fontSize: 13 }}>
                        Durée : {it.duree_consultation} min
                      </div>
                    </div>

                    <button
                      className="btnDangerOutline"
                      onClick={() => setConfirmDelete(it)}
                      title="Supprimer cette plage"
                    >
                      Supprimer
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="adminPlaceholder">
                <div className="adminPlaceholderTitle">Aucune disponibilité</div>
                <ul className="adminList">
                  <li>Ajoutez une ou plusieurs plages</li>
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal create */}
      {modalOpen && (
        <div className="logoutModalOverlay">
          <div className="logoutModal" style={{ maxWidth: 560 }}>
            <div className="logoutModalTitle">Ajouter une disponibilité</div>
            <div className="logoutModalText">Définissez une plage (jour + heures + durée).</div>

            <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
              <label className="field">
                <div className="fieldLabel">Jour</div>
                <div className="input">
                  <select
                    className="inputControl"
                    value={items.jour_semaine}
                    onChange={(e) => setForm((s) => ({ ...s, jour_semaine: Number(e.target.value) }))}
                  >
                    {DAYS.map((d) => (
                      <option key={d.k} value={d.k}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <div className="grid2">
                <label className="field">
                  <div className="fieldLabel">Heure début</div>
                  <div className="input">
                    <input
                      className="inputControl"
                      type="time"
                      value={form.heure_debut}
                      onChange={(e) => setForm((s) => ({ ...s, heure_debut: e.target.value }))}
                    />
                  </div>
                </label>

                <label className="field">
                  <div className="fieldLabel">Heure fin</div>
                  <div className="input">
                    <input
                      className="inputControl"
                      type="time"
                      value={form.heure_fin}
                      onChange={(e) => setForm((s) => ({ ...s, heure_fin: e.target.value }))}
                    />
                  </div>
                </label>
              </div>

              <label className="field">
                <div className="fieldLabel">Durée d’une consultation (minutes)</div>
                <div className="input">
                  <input
                    className="inputControl"
                    type="number"
                    min="5"
                    max="240"
                    value={form.duree_consultation}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, duree_consultation: Number(e.target.value) }))
                    }
                  />
                </div>
              </label>
            </div>

            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setModalOpen(false)}>
                Annuler
              </button>
              <button className="btnPrimary" onClick={saveDisponibilite}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Confirmation</div>
            <div className="logoutModalText">
              Supprimer la plage : <b>{dayLabel(confirmDelete.jour_semaine)}</b>{" "}
              {confirmDelete.heure_debut} → {confirmDelete.heure_fin} ?
            </div>

            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setConfirmDelete(null)}>
                Annuler
              </button>
              <button className="btnDanger" onClick={doDelete}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
       {toast.msg && (
  <div className={`adminToast ${toast.type}`}>
    {toast.msg}
  </div>
)}
    </div>
  );
}